import React, { useContext, useState, useEffect } from 'react';
import { useGlobalContext } from 'GlobalContext';
import { useSettings } from 'services/settings/SettingsContext';
import FormFieldContext from '../FormFieldContext';
import { DateFieldType, FieldData } from 'types/FieldTypes';
import FieldWrapper from '../FieldWrapper';
import DateInput from '../basefields/DateInput';
import FieldViewMode from './elements/FieldViewMode';
import '../../../style/scss/live-edit.scss';
import '../../../style/scss/forms.scss';
import '../../../style/scss/tooltip.scss';
import "react-datepicker/dist/react-datepicker.css";

interface DateFieldProps extends DateFieldType {
    shouldAutoFocus?: boolean;
    errorMessage?: string | string[] | undefined;
}

const DateField: React.FC<DateFieldProps & { data: FieldData, viewKey: string }> = ({ 
    viewKey, name, label, data, convertTimezone, utcDatetime, localDatetime, helperText, 
    tooltipText, defaultValue, placeholder, viewInEditMode, showIfFilled, isEditable, 
    disabled, alignment
}) => {
    const { editing, updatedData, setUpdatedData } = useContext(FormFieldContext);
    const { setUnsavedChanges } = useGlobalContext();
    const { userLocale } = useSettings();
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);

    // Tracks wheter the user has actually edited this field, as opposed to updatedData[name]
    // merely being set because a defaultValue was just programmatically applied below. DateInput
    // uses this to know when it's safe to still derive its own display state from the value prop -
    // if it were based on updatedData[name] !== undefined instead, applying a defaultValue for the
    // first time would immediately look like "already edited" and DateInput would never show it.
    const [isUserEdited, setIsUserEdited] = useState(false);

    // Set the current value from the received data
    useEffect(() => {
        if (data && data[name]) {
            const dateValue = new Date(data[name]);

            // Check if the date value is a legit date object
            if (!isNaN(dateValue.getTime())) {
                setSelectedDate(dateValue);
            } else {
                setSelectedDate(null);
            }
        } else if (defaultValue) {
            const dateValue = new Date(defaultValue);

            // Set the default value in the updated data
            // setUpdatedData({ ...updatedData, [name]: defaultValue });

            setUpdatedData((prevData: any) => ({
                ...prevData,
                [name]: defaultValue,
            }));

            // Check if the date value is a legit date object
            if (!isNaN(dateValue.getTime())) {

                // Set the default value as selected date
                setSelectedDate(dateValue);
            };
        }
    }, [data, name, defaultValue]);

    // Don't show the field if it only may be visible if it contains a value
    if (showIfFilled && (!selectedDate)) {
        return null;
    }

    // Handles the date change
    const handleDateChange = (dateString: string | null) => {
        setIsUserEdited(true);

        if (dateString === null) {
            // If the date is deleted, set it to null
            setSelectedDate(null);
            setUpdatedData({ ...updatedData, [name]: null })
        } else {
            const parsedDate = new Date(dateString);
            if (!isNaN(parsedDate.getTime())) {
                setSelectedDate(parsedDate);
            } else {
                setSelectedDate(null);
            }
        }
    
        // If there are changes, set unsaved changes and updated data
        const hasChanged = dateString !== (data[name] ? new Date(data[name]).toISOString().split('T')[0] : null);
        setUnsavedChanges(viewKey, hasChanged);
        if (hasChanged) {
            setUpdatedData({ ...updatedData, [name]: dateString });
        }
    };

    // Format the selectedDate
    const fieldValue = selectedDate && selectedDate.toLocaleDateString(userLocale, {year: 'numeric', month: '2-digit', day: '2-digit'});

    return (
        <FieldWrapper
            name={name}
            label={label}
            tooltipText={tooltipText}
            helperText={helperText}
            isEditable={isEditable}
            disabled={disabled}
            viewInEditMode={viewInEditMode}
            alignment={alignment}>
            {(editing || viewInEditMode) ? (
                // Edit mode
                <DateInput
                    id={name}
                    name={name}
                    value={selectedDate}
                    convertTimezone={convertTimezone}
                    utcDatetime={utcDatetime}
                    localDatetime={localDatetime}
                    onChange={(date) => handleDateChange(date)}
                    placeholder={placeholder}
                    hasUpdatedValue={isUserEdited}
                    disabled={disabled}
                />
            ) : (
                // View mode
                <FieldViewMode 
                    value={fieldValue} 
                    alignment={alignment}
                />
            )}
        </FieldWrapper>
    );
};

export default DateField;